import React, { useState, useEffect } from 'react';
import { FileText, Plus, Upload, Sparkles, Loader2, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { ppaAPI, exportAPI } from '../../services/api';
import ExportMenu from '../../components/ExportMenu';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const STATUS_COLOR = {
  active:     'bg-emerald-100 text-emerald-700',
  expired:    'bg-gray-100 text-gray-500',
  terminated: 'bg-red-100 text-red-700',
  draft:      'bg-amber-100 text-amber-700',
};

const RISK_COLOR = {
  low:    'bg-emerald-100 text-emerald-700',
  medium: 'bg-amber-100 text-amber-700',
  high:   'bg-red-100 text-red-700',
};

function Modal({ title, onClose, children, onSubmit, loading, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={clsx('bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] flex flex-col', wide?'max-w-2xl':'max-w-lg')}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">{children}</div>
        {onSubmit && <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={onSubmit} disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin"/>} Save Contract
          </button>
        </div>}
      </div>
    </div>
  );
}

const F = ({ label, children }) => <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>;
const I = (p) => <input {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>;
const S = ({ children, ...p }) => <select {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">{children}</select>;

export default function PPAPage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('super_admin','finance');

  const [ppas, setPpas]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatus] = useState('active');
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [createModal, setCreate]  = useState(false);
  const [analyzeModal, setAnalyze] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  const [analyzeFile, setAnalyzeFile] = useState(null);

  const initForm = { contract_code:'', counterparty_name:'', counterparty_country:'', contract_type:'physical', status:'active', volume_mwh:'', price_eur_mwh:'', indexation:'fixed', territory:'', start_date:'', end_date:'', signing_date:'', contract_value_eur:'', risk_rating:'medium', notes:'' };
  const [form, setForm] = useState(initForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await ppaAPI.list({ status: statusFilter||undefined, search: search||undefined });
      setPpas(data.data.items || data.data);
      setTotal(data.data.pagination?.total || (data.data.items||data.data).length);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, search]);

  const loadDetail = async (id) => {
    try { const { data } = await ppaAPI.get(id); setSelected(data.data); }
    catch {}
  };

  const save = async () => {
    setSaving(true);
    try {
      await ppaAPI.create(form);
      toast.success('PPA contract created');
      setCreate(false);
      setForm(initForm);
      load();
    } catch {} finally { setSaving(false); }
  };

  const analyze = async () => {
    if (!analyzeFile) { toast.error('Please select a file'); return; }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('file', analyzeFile);
      const { data } = await ppaAPI.analyzeFile(fd);
      setAnalysisResult(data.data.analysis);
      toast.success('Analysis complete');
    } catch {} finally { setAnalyzing(false); }
  };

  // Portfolio totals
  const totalValue = ppas.filter(p=>p.status==='active').reduce((a,p)=>a+parseFloat(p.contract_value_eur||0),0);
  const expiringSoon = ppas.filter(p=>p.status==='active'&&p.days_to_expiry<=90&&p.days_to_expiry>=0).length;

  const set = (k) => (e) => setForm(f=>({...f,[k]:e.target.value}));

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">PPA Contract Management</h1>
          <p className="text-sm text-gray-500">{total} contracts · Active portfolio value: €{(totalValue/1000000).toFixed(1)}M</p>
        </div>
        <div className="flex gap-2">
          <ExportMenu exports={[{ label:'Excel', apiFn: exportAPI.ppaExcel, filename:'ppa-contracts.xlsx' }]}/>
          {canWrite && <>
            <button onClick={()=>setAnalyze(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50">
              <Sparkles size={14}/> AI Analyze
            </button>
            <button onClick={()=>setCreate(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600">
              <Plus size={14}/> Add PPA
            </button>
          </>}
        </div>
      </div>

      {/* Alerts */}
      {expiringSoon>0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3.5 flex items-center gap-3">
          <AlertTriangle size={16} className="text-amber-600 shrink-0"/>
          <p className="text-sm text-amber-800 font-medium">{expiringSoon} contract{expiringSoon>1?'s':''} expiring within 90 days</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 items-center">
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search counterparty or code…"
          className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>
        <div className="flex gap-1">
          {['','active','expired','draft','terminated'].map(s=>(
            <button key={s} onClick={()=>setStatus(s)}
              className={clsx('px-3 py-1.5 text-xs rounded-lg font-medium capitalize transition-colors',
                statusFilter===s?'bg-primary-500 text-white':'bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100')}>
              {s||'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Table + detail side panel */}
      <div className={clsx('grid gap-4', selected?'grid-cols-1 lg:grid-cols-2':'grid-cols-1')}>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>{['Contract','Counterparty','Volume MWh','Price €/MWh','Expires','Status','Risk'].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? <tr><td colSpan={7} className="text-center py-12"><Loader2 size={20} className="animate-spin text-gray-400 mx-auto"/></td></tr>
                : ppas.length===0 ? <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">No contracts found.</td></tr>
                : ppas.map((p,i)=>(
                  <tr key={p.id} onClick={()=>loadDetail(p.id)}
                    className={clsx('hover:bg-hover cursor-pointer transition-colors', i%2===0?'bg-white':'bg-surface', selected?.id===p.id&&'ring-2 ring-inset ring-primary-500/30')}>
                    <td className="px-4 py-3 font-medium text-gray-900">{p.contract_code}</td>
                    <td className="px-4 py-3"><p className="font-medium text-gray-900">{p.counterparty_name}</p><p className="text-xs text-gray-400">{p.counterparty_country||''}</p></td>
                    <td className="px-4 py-3 text-gray-600">{p.volume_mwh?Number(p.volume_mwh).toLocaleString():'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{p.price_eur_mwh?`€${p.price_eur_mwh}`:'—'}</td>
                    <td className="px-4 py-3">
                      <p className="text-gray-600">{new Date(p.end_date).toLocaleDateString('en-GB')}</p>
                      {p.days_to_expiry>=0&&p.days_to_expiry<=90&&<p className={clsx('text-xs font-medium',p.days_to_expiry<=7?'text-danger':p.days_to_expiry<=30?'text-warn':'text-amber-600')}>{p.days_to_expiry}d remaining</p>}
                    </td>
                    <td className="px-4 py-3"><span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',STATUS_COLOR[p.status])}>{p.status}</span></td>
                    <td className="px-4 py-3"><span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',RISK_COLOR[p.risk_rating])}>{p.risk_rating}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{selected.counterparty_name}</h3>
                <p className="text-xs text-gray-400">{selected.contract_code}</p>
              </div>
              <button onClick={()=>setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="space-y-2 text-sm">
              {[['Type',selected.contract_type],['Volume',selected.volume_mwh?`${Number(selected.volume_mwh).toLocaleString()} MWh`:'—'],
                ['Price',selected.price_eur_mwh?`€${selected.price_eur_mwh}/MWh`:'—'],
                ['Indexation',selected.indexation],['Territory',selected.territory||'—'],
                ['Start',new Date(selected.start_date).toLocaleDateString('en-GB')],
                ['End',new Date(selected.end_date).toLocaleDateString('en-GB')],
                ['Contract Value',selected.contract_value_eur?`€${Number(selected.contract_value_eur).toLocaleString()}`:'—'],
              ].map(([l,v])=>(
                <div key={l} className="flex justify-between py-1.5 border-b border-gray-50 last:border-0">
                  <span className="text-gray-500">{l}</span>
                  <span className="font-medium text-gray-900 capitalize">{v}</span>
                </div>
              ))}
            </div>
            {selected.ai_summary && (
              <div className="bg-primary-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-primary-700 mb-1 flex items-center gap-1"><Sparkles size={11}/>AI Summary</p>
                <p className="text-xs text-gray-700 leading-relaxed">{selected.ai_summary}</p>
              </div>
            )}
            {selected.notes && <p className="text-xs text-gray-500 italic">{selected.notes}</p>}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {createModal && <Modal title="New PPA Contract" onClose={()=>setCreate(false)} onSubmit={save} loading={saving} wide>
        <div className="grid grid-cols-2 gap-4">
          <F label="Contract Code *"><I value={form.contract_code} onChange={set('contract_code')} placeholder="PPA-2024-001"/></F>
          <F label="Counterparty Name *"><I value={form.counterparty_name} onChange={set('counterparty_name')}/></F>
          <F label="Country"><I value={form.counterparty_country} onChange={set('counterparty_country')} placeholder="DE"/></F>
          <F label="Contract Type"><S value={form.contract_type} onChange={set('contract_type')}>
            <option value="physical">Physical</option><option value="financial">Financial</option>
            <option value="virtual">Virtual</option><option value="sleeved">Sleeved</option>
          </S></F>
          <F label="Volume (MWh)"><I type="number" value={form.volume_mwh} onChange={set('volume_mwh')}/></F>
          <F label="Price (€/MWh)"><I type="number" step="0.0001" value={form.price_eur_mwh} onChange={set('price_eur_mwh')}/></F>
          <F label="Indexation"><S value={form.indexation} onChange={set('indexation')}>
            <option value="fixed">Fixed</option><option value="cpi">CPI</option><option value="ppi">PPI</option><option value="custom">Custom</option>
          </S></F>
          <F label="Territory"><I value={form.territory} onChange={set('territory')} placeholder="e.g. Germany"/></F>
          <F label="Start Date"><I type="date" value={form.start_date} onChange={set('start_date')}/></F>
          <F label="End Date"><I type="date" value={form.end_date} onChange={set('end_date')}/></F>
          <F label="Contract Value (€)"><I type="number" value={form.contract_value_eur} onChange={set('contract_value_eur')}/></F>
          <F label="Risk Rating"><S value={form.risk_rating} onChange={set('risk_rating')}>
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
          </S></F>
          <F label="Status"><S value={form.status} onChange={set('status')}>
            <option value="active">Active</option><option value="draft">Draft</option>
          </S></F>
        </div>
        <F label="Notes"><textarea value={form.notes} onChange={set('notes')} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/30"/></F>
      </Modal>}

      {/* AI Analyze Modal */}
      {analyzeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2"><Sparkles size={16} className="text-primary-500"/>AI PPA Analysis</h2>
              <button onClick={()=>{setAnalyze(false);setAnalysisResult('');setAnalyzeFile(null);}} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4">
              <p className="text-sm text-gray-500">Upload a PPA PDF and AI will extract key terms, flag unusual clauses, and assess risk.</p>
              <label className={clsx('flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 cursor-pointer transition-colors',analyzeFile?'border-primary-400 bg-primary-50':'border-gray-200 hover:border-primary-300 hover:bg-gray-50')}>
                <Upload size={24} className={analyzeFile?'text-primary-500':'text-gray-400'} />
                <p className="text-sm font-medium mt-2 text-gray-700">{analyzeFile?analyzeFile.name:'Click to upload PPA PDF'}</p>
                <p className="text-xs text-gray-400 mt-1">PDF up to 50MB</p>
                <input type="file" accept=".pdf" className="hidden" onChange={e=>setAnalyzeFile(e.target.files[0])}/>
              </label>
              {!analysisResult && <button onClick={analyze} disabled={analyzing||!analyzeFile}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
                {analyzing?<Loader2 size={14} className="animate-spin"/>:<Sparkles size={14}/>}
                {analyzing?'Analysing…':'Run AI Analysis'}
              </button>}
              {analysisResult && (
                <div className="bg-primary-50 border border-primary-100 rounded-xl p-5 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{analysisResult}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
